/**
 * Phase 2B.2 — Question Navigator with drag-and-drop reorder.
 *
 *   A4 (Question Navigator): structured tree of sections + content with
 *       reserved status indicator slots (complete/warning/error). Status
 *       sources arrive in Phase 2C from the validator engine; the slot
 *       architecture ships in 2B.2.
 *   DnD (@dnd-kit/sortable): sections reorder vertically; content items
 *       reorder within and across sections.
 */
import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  DndContext, closestCenter, KeyboardSensor, PointerSensor, useSensor, useSensors,
  DragOverlay, defaultDropAnimation,
} from '@dnd-kit/core';
import {
  arrayMove, SortableContext, sortableKeyboardCoordinates,
  verticalListSortingStrategy, useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  ChevronDown, ChevronRight, Compass, FileText, GripVertical, Plus,
  CheckCircle2, AlertTriangle, AlertCircle,
} from 'lucide-react';
import { Button } from '@/components/ui/button';

/* ============================================================================
 *                    Status indicator slot (A4 architecture)
 * ========================================================================== */
function StatusIndicator({ status }) {
  // Phase 2B.2 reserves the slot. The validator wires real statuses in 2C.
  if (!status) return <span className="w-3 h-3 inline-block" aria-hidden />;
  if (status === 'complete') return <CheckCircle2 className="h-3 w-3 text-success" data-testid="studio-status-complete" />;
  if (status === 'warning') return <AlertTriangle className="h-3 w-3 text-amber-500" data-testid="studio-status-warning" />;
  if (status === 'error') return <AlertCircle className="h-3 w-3 text-destructive" data-testid="studio-status-error" />;
  return null;
}

/* ============================================================================
 *                        Sortable section row
 * ========================================================================== */
function SortableSection({ id, sectionIndex, section, expanded, onToggle, onFocus, focused, contentCount, status }) {
  const { t } = useTranslation();
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id, data: { type: 'section' } });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.4 : 1,
  };
  return (
    <div ref={setNodeRef} style={style} className="group" data-testid={`studio-sidebar-section-row-${section.id}`}>
      <div
        className={`flex items-center gap-1 px-1 py-1.5 rounded text-sm transition-all ${
          focused ? 'bg-primary/10 text-primary' : 'hover:bg-muted/60'
        }`}
      >
        <button
          type="button"
          {...attributes}
          {...listeners}
          className="cursor-grab active:cursor-grabbing text-muted-foreground/40 hover:text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity"
          data-testid={`studio-sidebar-section-handle-${section.id}`}
          aria-label={t('studio.editor.dragSection', { defaultValue: 'Drag to reorder section' })}
        >
          <GripVertical className="h-3 w-3" />
        </button>
        <button
          type="button"
          onClick={() => { onFocus({ kind: 'section', id: section.id }); onToggle(section.id); }}
          data-testid={`studio-sidebar-section-${section.id}`}
          className="flex items-center gap-1 flex-1 text-left min-w-0"
        >
          {expanded ? <ChevronDown className="h-3 w-3 shrink-0" /> : <ChevronRight className="h-3 w-3 shrink-0" />}
          <StatusIndicator status={status} />
          <span className="font-medium whitespace-normal break-words leading-snug line-clamp-2" title={section.name || ''}>
            {sectionIndex + 1}. {section.name || t('studio.editor.untitledSection', { defaultValue: 'Untitled section' })}
          </span>
        </button>
        <span className="text-xs text-muted-foreground tabular-nums">{contentCount}</span>
      </div>
    </div>
  );
}

/* ============================================================================
 *                        Sortable content row
 * ========================================================================== */
function SortableContent({ id, sectionIndex, contentIndex, question, focused, onFocus, status }) {
  const { t } = useTranslation();
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id, data: { type: 'content', section_id: question.section_id } });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.4 : 1,
  };
  const stable = question.stable_id;
  return (
    <li ref={setNodeRef} style={style} className="group" data-testid={`studio-sidebar-content-row-${question.id}`}>
      <div
        className={`flex items-center gap-1 px-1 py-1 rounded text-xs transition-all ${
          focused ? 'bg-primary/10 text-primary' : 'hover:bg-muted/60'
        }`}
      >
        <button
          type="button"
          {...attributes}
          {...listeners}
          className="cursor-grab active:cursor-grabbing text-muted-foreground/40 hover:text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity"
          data-testid={`studio-sidebar-content-handle-${question.id}`}
          aria-label={t('studio.editor.dragContent', { defaultValue: 'Drag to reorder content' })}
        >
          <GripVertical className="h-3 w-3" />
        </button>
        <button
          type="button"
          onClick={() => onFocus({ kind: 'content', id: question.id })}
          data-testid={`studio-sidebar-content-${question.id}`}
          className="flex items-center gap-1 flex-1 text-left min-w-0"
        >
          <FileText className="h-3 w-3 shrink-0 text-muted-foreground" />
          <StatusIndicator status={status} />
          {stable && (
            <span
              className="font-mono text-xs px-1 rounded bg-muted/60 text-muted-foreground shrink-0"
              data-testid={`studio-content-stable-id-${question.id}`}
            >
              {stable}
            </span>
          )}
          <span className="truncate" title={question.prompt || ''}>
            Q{sectionIndex + 1}.{contentIndex + 1}
            <span className="text-muted-foreground ml-1.5">
              {(question.prompt || '').slice(0, 40)}
            </span>
          </span>
        </button>
      </div>
    </li>
  );
}

/* ============================================================================
 *                        Question Navigator
 * ========================================================================== */
export function QuestionNavigator({
  sections,
  questions,
  focus,
  editable,
  onFocus,
  onAddSection,
  onReorderSections,
  onReorderQuestion,
  onBackToBlueprint,
  contentStatus,    // optional: { [qid]: 'complete' | 'warning' | 'error' }
  sectionStatus,    // optional: { [sid]: 'complete' | 'warning' | 'error' }
}) {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(() => new Set(sections.map((s) => s.id)));
  const [activeDrag, setActiveDrag] = useState(null);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const sectionIds = useMemo(() => sections.map((s) => s.id), [sections]);

  const toggleSection = (sid) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(sid)) next.delete(sid);
      else next.add(sid);
      return next;
    });
  };

  const handleDragStart = (e) => setActiveDrag(e.active);
  const handleDragEnd = (e) => {
    setActiveDrag(null);
    const { active, over } = e;
    if (!over) return;
    const aData = active.data.current || {};
    const oData = over.data.current || {};
    if (aData.type === 'section' && oData.type === 'section') {
      const oldIdx = sectionIds.indexOf(active.id);
      const newIdx = sectionIds.indexOf(over.id);
      if (oldIdx !== newIdx) {
        const reordered = arrayMove(sections, oldIdx, newIdx).map((s, i) => ({ ...s, order: i }));
        onReorderSections(reordered);
      }
    } else if (aData.type === 'content') {
      // Move/reorder content. If dropped onto another content item, insert
      // before it in the same section. If dropped onto a section header,
      // append to that section.
      const sourceQ = questions.find((q) => q.id === active.id);
      if (!sourceQ) return;
      let targetSectionId = sourceQ.section_id;
      let targetIndex = null;
      if (oData.type === 'content') {
        const targetQ = questions.find((q) => q.id === over.id);
        if (targetQ) {
          targetSectionId = targetQ.section_id;
          // Compute insert index within the target section
          const inSection = questions.filter((q) => q.section_id === targetSectionId && q.id !== active.id);
          targetIndex = inSection.findIndex((q) => q.id === over.id);
        }
      } else if (oData.type === 'section') {
        targetSectionId = over.id;
        targetIndex = null;  // append
      }
      onReorderQuestion(active.id, targetSectionId, targetIndex);
    }
  };

  return (
    <div className="flex-1 overflow-y-auto" data-testid="studio-sidebar-outline">
      <div className="p-3">
        <Button
          variant="ghost"
          size="sm"
          className="w-full justify-start mb-2 -ml-2"
          onClick={onBackToBlueprint}
          data-testid="studio-sidebar-blueprint-link"
        >
          <Compass className="h-4 w-4 mr-1.5" />
          {t('studio.editor.blueprint', { defaultValue: 'Assessment Blueprint' })}
        </Button>

        <div className="flex items-center justify-between mb-2 px-1">
          <p className="text-xs uppercase tracking-wider font-semibold text-muted-foreground">
            {t('studio.editor.navigator', { defaultValue: 'Navigator' })}
          </p>
          {editable && (
            <Button
              variant="ghost"
              size="sm"
              className="h-6 px-1.5"
              onClick={onAddSection}
              data-testid="studio-sidebar-add-section"
            >
              <Plus className="h-3 w-3 mr-0.5" />
              <span className="text-xs">{t('studio.editor.addSection', { defaultValue: 'Section' })}</span>
            </Button>
          )}
        </div>

        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragStart={handleDragStart}
          onDragEnd={handleDragEnd}
        >
          <SortableContext items={sectionIds} strategy={verticalListSortingStrategy} disabled={!editable}>
            <ul className="space-y-0.5" role="tree" data-testid="studio-navigator-tree">
              {sections.map((s, sIdx) => {
                const sectionQs = questions.filter((q) => q.section_id === s.id)
                                           .sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
                const isExpanded = expanded.has(s.id);
                const sectionFocused = focus?.kind === 'section' && focus?.id === s.id;
                return (
                  <li key={s.id} role="treeitem" aria-expanded={isExpanded}>
                    <SortableSection
                      id={s.id}
                      sectionIndex={sIdx}
                      section={s}
                      expanded={isExpanded}
                      onToggle={toggleSection}
                      onFocus={onFocus}
                      focused={sectionFocused}
                      contentCount={sectionQs.length}
                      status={sectionStatus?.[s.id]}
                    />
                    {isExpanded && sectionQs.length > 0 && (
                      <SortableContext
                        items={sectionQs.map((q) => q.id)}
                        strategy={verticalListSortingStrategy}
                        disabled={!editable}
                      >
                        <ul className="ml-5 mt-0.5 space-y-0.5 border-l border-border pl-2" role="group">
                          {sectionQs.map((q, qIdx) => {
                            const contentFocused = focus?.kind === 'content' && focus?.id === q.id;
                            return (
                              <SortableContent
                                key={q.id}
                                id={q.id}
                                sectionIndex={sIdx}
                                contentIndex={qIdx}
                                question={q}
                                focused={contentFocused}
                                onFocus={onFocus}
                                status={contentStatus?.[q.id]}
                              />
                            );
                          })}
                        </ul>
                      </SortableContext>
                    )}
                  </li>
                );
              })}
            </ul>
          </SortableContext>
          <DragOverlay dropAnimation={defaultDropAnimation}>
            {activeDrag ? <div className="px-2 py-1 bg-card border border-primary/40 rounded shadow text-xs">{t('studio.editor.dragging', { defaultValue: 'Moving…' })}</div> : null}
          </DragOverlay>
        </DndContext>

        {sections.length === 0 && (
          <p className="text-xs text-muted-foreground px-2 py-3" data-testid="studio-sidebar-empty">
            {t('studio.editor.emptyOutline', { defaultValue: 'No content yet.' })}
          </p>
        )}
      </div>
    </div>
  );
}
